import { FC, useEffect, useMemo, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';

import { Box } from '@mui/material';

import { Text } from '@/components/Shared/Text';

import { SimulationUsageInfoDto } from '../hooks/useGetTokenUsageReport';
import { useResizableColumns } from '../hooks/useResizableColumns';

import { CellContextMenu } from './CellContextMenu';
import { ResizableDivider } from './ResizableDivider';

import styles from '../styles.module.scss';

type SDKUsageGridProps = {
  tokenUsage: SimulationUsageInfoDto[];
};

const NoRowsOverlay = () => (
  <Box component="div" display="flex" alignItems="center" justifyContent="center" height="100%">
    <Text type="medium-12px">No token usage data found for the selected time period</Text>
  </Box>
);

const formatRuntime = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const getProjectColumns = (): GridColDef<SimulationUsageInfoDto>[] => [
  {
    field: 'projectName',
    headerName: 'Project',
    width: 200,
    headerClassName: styles['data-grid-header'],
  },
  {
    field: 'totalUsage',
    headerName: 'Total Usage',
    width: 150,
    valueFormatter: (params) => `${(params.value as number).toFixed(2)} tokens`,
    headerClassName: styles['data-grid-header'],
  },
  {
    field: 'simulationCount',
    headerName: 'Simulations',
    width: 120,
    valueGetter: (params) => params.row.simulationUsages?.length || 0,
    headerClassName: styles['data-grid-header'],
  },
  {
    field: 'lastCompleted',
    headerName: 'Last Completed',
    width: 180,
    valueGetter: (params) => {
      const dates = params.row.simulationUsages?.map((s) => s.completedAt).filter(Boolean) as string[];
      return dates?.length ? Math.max(...dates.map((d) => new Date(d).getTime())) : null;
    },
    valueFormatter: (params) => (params.value ? dayjs(params.value as number).format('DD/MM/YYYY HH:mm') : '-'),
    headerClassName: styles['data-grid-header'],
  },
];

const getSimulationColumns = (): GridColDef[] => [
  {
    field: 'simulationId',
    headerName: 'Simulation ID',
    width: 200,
    headerClassName: styles['data-grid-header'],
  },
  {
    field: 'simulationName',
    headerName: 'Simulation Name',
    width: 200,
    headerClassName: styles['data-grid-header'],
  },
  {
    field: 'usage',
    headerName: 'Usage',
    width: 150,
    valueFormatter: (params) => `${(params.value as number).toFixed(2)} tokens`,
    headerClassName: styles['data-grid-header'],
  },
  {
    field: 'runtime',
    headerName: 'Runtime',
    width: 120,
    valueFormatter: (params) =>
      params.value != null ? formatRuntime(Math.floor(params.value as number)) : '-',
    headerClassName: styles['data-grid-header'],
  },
  {
    field: 'maxGPUCount',
    headerName: 'GPU Count',
    width: 120,
    valueFormatter: (params) => (params.value != null ? (params.value as number) : '-'),
    headerClassName: styles['data-grid-header'],
  },
  {
    field: 'completedAt',
    headerName: 'Completed',
    width: 180,
    valueFormatter: (params) => (params.value ? dayjs(params.value as string).format('DD/MM/YYYY HH:mm') : '-'),
    headerClassName: styles['data-grid-header'],
  },
  {
    field: 'email',
    headerName: 'Run by',
    width: 200,
    valueFormatter: (params) => (params.value as string) || '-',
    headerClassName: styles['data-grid-header'],
  },
];

export const SDKUsageGrid: FC<SDKUsageGridProps> = ({ tokenUsage }) => {
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [leftPanelFraction, setLeftPanelFraction] = useState(0.5);

  const projectColumns = useMemo(() => getProjectColumns(), []);
  const simulationColumns = useMemo(() => getSimulationColumns(), []);

  const {
    columns: resizableProjectColumns,
    containerRef: projectGridRef,
    containerNode: projectGridNode,
  } = useResizableColumns(projectColumns);
  const {
    columns: resizableSimulationColumns,
    containerRef: simulationGridRef,
    containerNode: simulationGridNode,
  } = useResizableColumns(simulationColumns);

  useEffect(() => {
    if (selectedProject && !tokenUsage.find((p) => p.projectId === selectedProject)) {
      setSelectedProject(null);
    }
  }, [tokenUsage, selectedProject]);

  const selectedProjectData = selectedProject ? tokenUsage.find((p) => p.projectId === selectedProject) : null;

  return (
    <div style={{ height: '100%', display: 'flex', gap: '4px' }}>
      <div ref={projectGridRef} style={{ flex: `${leftPanelFraction} 1 0`, minWidth: 0 }}>
        <CellContextMenu container={projectGridNode} />
        <DataGrid
          rows={tokenUsage}
          columns={resizableProjectColumns}
          getRowId={(row) => row.projectId}
          autoPageSize
          disableColumnSelector
          onRowClick={(params) => setSelectedProject(params.row.projectId)}
          rowSelectionModel={selectedProject ? [selectedProject] : []}
          initialState={{
            sorting: { sortModel: [{ field: 'lastCompleted', sort: 'desc' }] },
          }}
          slots={{ noRowsOverlay: NoRowsOverlay }}
          slotProps={{ row: { className: styles['data-grid-row'] } }}
          rowHeight={42}
          columnHeaderHeight={42}
          sx={{
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: '#2b8f77',
              '&:hover': { backgroundColor: '#2b8f77' },
            },
            '& .MuiDataGrid-columnSeparator': {
              cursor: 'col-resize',
              '&:hover': { color: 'rgba(43, 143, 119, 0.8)' },
            },
          }}
        />
      </div>
      {tokenUsage.length > 0 && (
        <>
          <ResizableDivider onResize={setLeftPanelFraction} />
          <div
            ref={simulationGridRef}
            style={{ flex: `${1 - leftPanelFraction} 1 0`, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <CellContextMenu container={simulationGridNode} />
            {selectedProject ? (
              <Box component="div" sx={{ flex: 1 }}>
                <DataGrid
                  rows={selectedProjectData?.simulationUsages || []}
                  columns={resizableSimulationColumns}
                  getRowId={(row) => row.simulationId}
                  autoPageSize
                  disableRowSelectionOnClick
                  disableColumnSelector
                  initialState={{
                    sorting: { sortModel: [{ field: 'completedAt', sort: 'desc' }] },
                  }}
                  slotProps={{ row: { className: styles['data-grid-row'] } }}
                  rowHeight={42}
                  columnHeaderHeight={42}
                  sx={{
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-columnSeparator': {
                      cursor: 'col-resize',
                      '&:hover': { color: 'rgba(43, 143, 119, 0.8)' },
                    },
                  }}
                />
              </Box>
            ) : (
              <Box
                component="div"
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid rgba(81, 81, 81, 1)',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                }}>
                <Text type="medium-12px">Select a project to view token usage per simulation</Text>
              </Box>
            )}
          </div>
        </>
      )}
    </div>
  );
};
